import React, { useState, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import OrgSelect from '../../../../components/OrgSelect/OrgSelect';

function SettingsTab({ task, project, progressRate, setProgressRate, handleSaveProgress, currentUser, onClose, onDeleteTask, hasEditPermission, categoryName }) {
  // 편집(수정) 모드 상태 관리
  const [isEditing, setIsEditing] = useState(false);

  // 폼 상태 관리 (수정용 데이터 셋업)
  const [formData, setFormData] = useState({
    title: task.title || '',
    description: task.description || '',
    startDate: task.start_date ? new Date(task.start_date) : null,
    endDate: task.end_date ? new Date(task.end_date) : null,
    status: task.status || 'pending',
    sortOrder: task.sort_order || 10,
  });

  const isManagerUser = (m) => m && (m.is_manager === true || m.is_manager === 1 || m.is_manager === '1' || m.is_manager === 'true');
  const isWatcherUser = (m) => m && (!m.is_manager || m.is_manager === false || m.is_manager === 0 || m.is_manager === '0' || m.is_manager === 'false');

  // 담당자 및 참조자 로컬 상태
  const [localMembers, setLocalMembers] = useState(task.members?.filter(isManagerUser) || []);
  const [localObservers, setLocalObservers] = useState(task.members?.filter(isWatcherUser) || []);

  // 조직도 모달 상태
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [orgTarget, setOrgTarget] = useState('members');

  // 커스텀 담당자 팝오버 상태
  const [showMemberPopover, setShowMemberPopover] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [tempMembers, setTempMembers] = useState([]);
  const [popoverPosition, setPopoverPosition] = useState('bottom');
  const popoverRef = useRef(null);

  const toggleTempMemberSelection = (user) => {
    const uid = String(user.userid || user.id);
    const exists = tempMembers.find(m => String(m.user_id) === uid);
    if (exists) {
      setTempMembers(prev => prev.filter(m => String(m.user_id) !== uid));
    } else {
      setTempMembers(prev => [...prev, {
        user_id: uid,
        is_manager: true,
        user: {
          id: uid,
          name: user.name,
          position: user.org?.position || user.position,
          org: user.org
        }
      }]);
    }
  };

  // 프로젝트 멤버 추출 로직
  const projectUsers = useMemo(() => {
    const users = [];
    if (project) {
      const addIfUnique = (u) => {
        if (u && u.id && !users.find(x => String(x.id) === String(u.id))) {
          users.push(u);
        }
      };
      addIfUnique(project.author);
      addIfUnique(project.reviewer);
      addIfUnique(project.leader);
      if (project.members && Array.isArray(project.members)) {
        project.members.forEach(m => addIfUnique(m.user));
      }
    }
    return users;
  }, [project]);

  const filteredProjectUsers = useMemo(() => {
    if (!memberSearchTerm) return projectUsers;
    return projectUsers.filter(u => u.name?.includes(memberSearchTerm));
  }, [projectUsers, memberSearchTerm]);

  // Click outside listener for Popover
  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setShowMemberPopover(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoverRef]);


  const getRandomColor = (name) => {
    // 우리나라 정서에 맞게 빨간색(red) 계열 완전 배제
    const colors = ['#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4', '#3F51B5', '#009688', '#795548', '#607D8B', '#0288D1', '#8E24AA', '#F57C00', '#388E3C'];
    let hash = 0;
    for (let i = 0; i < (name?.length || 0); i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(Math.imul(hash, 2654435761));
    return colors[hash % colors.length];
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  const handleRemoveMember = (userId, type) => {
    if (type === 'members') setLocalMembers(prev => prev.filter(m => m.user_id !== userId));
    else setLocalObservers(prev => prev.filter(m => m.user_id !== userId));
  };

  const handleConfirmOrgSelect = (selectedTargets) => {
    // OrgSelect에서 넘어온 targetObj(user)들을 포맷팅
    const newMembers = selectedTargets.map(t => ({
      user_id: t.target_id,
      is_manager: orgTarget === 'members',
      user: {
        userid: t.target_id,
        name: t.target_name,
        position: t.position,
        org: { dept_name: t.dept_name }
      }
    }));

    if (orgTarget === 'members') setLocalMembers(newMembers);
    else setLocalObservers(newMembers);
  };

  useEffect(() => {
    setFormData({
      title: task.title || '',
      description: task.description || '',
      startDate: task.start_date ? task.start_date.split('T')[0] : '',
      endDate: task.end_date ? task.end_date.split('T')[0] : '',
      status: task.status || 'pending',
      sortOrder: task.sort_order || 10,
    });

    // task가 변경될 때마다 로컬 멤버 및 참조자 상태도 최신화
    setLocalMembers(task.members?.filter(isManagerUser) || []);
    setLocalObservers(task.members?.filter(isWatcherUser) || []);
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'status') {
      if (value === 'completed') {
        setProgressRate(100);
      } else if (formData.status === 'completed' && value !== 'completed') {
        if (Number(progressRate) === 100) {
          setProgressRate(value === 'pending' ? 0 : 50);
        }
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const statusOptions = [
    { value: 'pending', label: '⏳ 시작 전' },
    { value: 'in_progress', label: '🔄 진행 중' },
    { value: 'completed', label: '✅ 완 료' }
  ];

  const labelStyle = { width: '110px', fontSize: '14px' };

  return (
    <div className="animation-fade-in d-flex flex-column h-100 bg-light">
      {/* 권한 경고 배너 */}
      {!hasEditPermission && (
        <div className="bg-white text-danger p-2 text-center fs-7 fw-bold border-bottom mb-2 mx-3 mt-3 rounded shadow-sm" style={{ border: '1px solid #f5c2c7' }}>
          <i className="fas fa-lock me-2"></i>조회 전용 모드 (담당자 또는 생성자만 수정 가능합니다)
        </div>
      )}

      {/* 스크롤 가능한 본문 영역 */}
      <div className="px-4 py-4 d-flex flex-column flex-grow-1" style={{ overflowY: 'auto' }}>

        {/* === [카드 1] 기본 정보 === */}
        <div className="bg-white p-4 rounded-3 shadow-sm border mb-4">
          
          {/* 분류 (읽기 전용) */}
          {categoryName && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-2">
                <span className="text-primary fw-bold me-2 fs-5"><i className="fas fa-layer-group"></i></span>
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>분류</h6>
              </div>
              <div className="bg-light border-0 py-2 px-3 rounded text-dark fw-medium fs-6">{categoryName}</div>
            </div>
          )}

          {/* 업무 제목 */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <span className="text-primary fw-bold me-2 fs-5">H</span>
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>업무 제목 <span className="text-danger">*</span></h6>
            </div>
            {isEditing ? (
              <input type="text" name="title" className="form-control border-0 py-2 px-3 fw-bold" style={{ backgroundColor: '#fff9e6' }} placeholder="수행할 세부 업무명을 입력하세요" value={formData.title} onChange={handleChange} />
            ) : (
              <div className="bg-light border-0 py-2 px-3 rounded text-dark fw-medium fs-6">{formData.title || '-'}</div>
            )}
            <p className="text-muted fs-8 mt-2 mb-0"><i className="fas fa-info-circle me-1"></i>구체적인 작업 단위로 명확하게 적어주세요.</p>
          </div>

          {/* 상세 설명 */}
          <div>
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-align-left text-primary fs-5 me-2"></i>
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>상세 설명</h6>
            </div>
            {isEditing ? (
              <textarea name="description" className="form-control bg-light border-0 p-3" rows="4" placeholder="세부 업무 내용을 상세히 작성해주세요" value={formData.description} onChange={handleChange} />
            ) : (
              <div className="bg-light border-0 p-3 rounded text-dark" style={{ minHeight: '100px', whiteSpace: 'pre-wrap' }}>{formData.description || '작성된 상세 설명이 없습니다.'}</div>
            )}
          </div>
        </div>

        {/* === [카드 2] 일정 및 상태 === */}
        <div className="bg-white p-4 rounded-3 shadow-sm border mb-4">
          <div className="row" style={{ rowGap: '20px' }}>
            {/* 시작일자 */}
            <div className="col-12 col-md-6 d-flex align-items-center">
              <div className="d-flex align-items-center flex-shrink-0" style={labelStyle}>
                <i className="fas fa-calendar-alt text-primary me-2"></i>
                <label className="fw-bold text-dark mb-0">시작일자 <span className="text-danger">*</span></label>
              </div>
              <div className="flex-grow-1">
                {isEditing ? (
                  <div className={`position-relative pms-datepicker-container ${isEditing ? 'pms-datepicker-required' : ''}`} style={{ maxWidth: '200px' }}>
                    <DatePicker selected={formData.startDate} onChange={(date) => handleDateChange(date, 'startDate')} dateFormat="yyyy-MM-dd" className="form-control border-0 py-2 text-dark fw-bold w-100 pe-5 date-cursor-pointer" placeholderText="날짜 선택" />
                    <i className="fas fa-calendar-day text-muted position-absolute" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}></i>
                  </div>
                ) : (
                  <div className="bg-light py-2 px-3 rounded text-secondary fs-7" style={{ maxWidth: '200px', minHeight: '38px' }}>
                    {formData.startDate ? (formData.startDate instanceof Date ? formData.startDate.toISOString().split('T')[0] : formData.startDate) : '-'}
                  </div>
                )}
              </div>
            </div>
            {/* 완료일자 */}
            <div className="col-12 col-md-6 d-flex align-items-center">
              <div className="d-flex align-items-center flex-shrink-0" style={labelStyle}>
                <i className="fas fa-calendar-check text-primary me-2"></i>
                <label className="fw-bold text-dark mb-0">목표일자 <span className="text-danger">*</span></label>
              </div>
              <div className="flex-grow-1">
                {isEditing ? (
                  <div className={`position-relative pms-datepicker-container ${isEditing ? 'pms-datepicker-required' : ''}`} style={{ maxWidth: '200px' }}>
                    <DatePicker selected={formData.endDate} onChange={(date) => handleDateChange(date, 'endDate')} dateFormat="yyyy-MM-dd" className="form-control border-0 py-2 text-dark fw-bold w-100 pe-5 date-cursor-pointer" placeholderText="날짜 선택" />
                    <i className="fas fa-calendar-day text-muted position-absolute" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}></i>
                  </div>
                ) : (
                  <div className="bg-light py-2 px-3 rounded text-secondary fs-7" style={{ maxWidth: '200px', minHeight: '38px' }}>
                    {formData.endDate ? (formData.endDate instanceof Date ? formData.endDate.toISOString().split('T')[0] : formData.endDate) : '-'}
                  </div>
                )}
              </div>
            </div>
            {/* 진행상태 */}
            <div className="col-12 col-md-6 d-flex align-items-center">
              <div className="d-flex align-items-center flex-shrink-0" style={labelStyle}>
                <i className="fas fa-tasks text-primary me-2"></i>
                <label className="fw-bold text-dark mb-0">진행상태</label>
              </div>
              <div className="flex-grow-1">
                {isEditing ? (
                  <select name="status" className="form-select bg-light border-0 py-2 fw-bold text-dark w-100" style={{ maxWidth: '200px' }} value={formData.status} onChange={handleChange}>
                    {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (
                  <div className="bg-light px-3 py-2 rounded-3 fw-bold text-dark d-flex align-items-center" style={{ maxWidth: '200px', minHeight: '38px' }}>
                    {statusOptions.find(opt => opt.value === formData.status)?.label || '⏳ 시작 전'}
                  </div>
                )}
              </div>
            </div>
            {/* 정렬순서 */}
            <div className="col-12 col-md-6 d-flex align-items-center">
              <div className="d-flex align-items-center flex-shrink-0" style={labelStyle}>
                <i className="fas fa-sort-numeric-down text-primary me-2"></i>
                <label className="fw-bold text-dark mb-0">정렬순서</label>
              </div>
              <div className="flex-grow-1">
                {isEditing ? (
                  <input type="number" name="sortOrder" className="form-control bg-light border-0 py-2 w-100" style={{ maxWidth: '200px' }} value={formData.sortOrder} onChange={handleChange} />
                ) : (
                  <div className="bg-light px-3 py-2 rounded-3 fw-bold text-dark d-flex align-items-center" style={{ maxWidth: '200px', minHeight: '38px' }}>
                    {formData.sortOrder || '-'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* === [카드 3] 진척도 및 인원 === */}
        <div className="bg-white p-4 rounded-3 shadow-sm border mb-2">
          {/* 완료율 */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-chart-line text-primary fs-5 me-2"></i>
              <label className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>완료율 (%)</label>
            </div>
            <div className="d-flex align-items-center gap-3 bg-light p-3 rounded-3">
              <input
                type="range"
                className="custom-progress-range flex-grow-1"
                min="0"
                max="100"
                step="5"
                value={progressRate}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setProgressRate(val);
                  if (val === 100) {
                    setFormData(prev => ({ ...prev, status: 'completed' }));
                  } else if (formData.status === 'completed' && val < 100) {
                    setFormData(prev => ({ ...prev, status: val === 0 ? 'pending' : 'in_progress' }));
                  }
                }}
                disabled={!isEditing}
                style={{
                  background: `linear-gradient(to right, #4c6ef5 ${progressRate}%, #dee2e6 ${progressRate}%)`
                }}
              />
              <div className="text-primary fw-bold fs-5" style={{ minWidth: '45px', textAlign: 'right' }}>{progressRate}%</div>
            </div>
          </div>

          <style>{`
            .custom-progress-range { -webkit-appearance: none; height: 8px; border-radius: 4px; outline: none; transition: background 0.2s; cursor: pointer; }
            .custom-progress-range::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #4c6ef5; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3); cursor: pointer; }
            .custom-progress-range:disabled { cursor: not-allowed; opacity: 0.8; }
            .custom-progress-range:disabled::-webkit-slider-thumb { background: #6c757d; cursor: not-allowed; }
            .member-scroll-box { background-color: #f8f9fa; border-radius: 8px; padding: 12px; min-height: 100px; max-height: 200px; overflow-y: auto; display: flex; flex-wrap: wrap; align-content: flex-start; gap: 8px; border: 1px solid #e9ecef; }
            .member-scroll-box::-webkit-scrollbar { width: 6px; }
            .member-scroll-box::-webkit-scrollbar-track { background: transparent; }
            .member-scroll-box::-webkit-scrollbar-thumb { background-color: #ced4da; border-radius: 10px; }
            .member-scroll-box::-webkit-scrollbar-thumb:hover { background-color: #adb5bd; }
            .pms-datepicker-required input { background-color: #fff9e6 !important; }
            .member-badge { background-color: #ffffff; color: #495057; border: 1px solid #dee2e6; border-radius: 6px; padding: 8px 12px; font-size: 0.85rem; font-weight: 500; display: flex; align-items: center; justify-content: space-between; width: calc(33.33% - 5.5px); box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
            .member-badge-info { display: flex; align-items: center; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
            .date-cursor-pointer { cursor: pointer !important; }
            .hover-bg-light:hover { background-color: #f8f9fa !important; }
            .cursor-pointer { cursor: pointer; }
            /* PMS 달력 전용 스타일 격리 */
            .pms-datepicker-container .react-datepicker-wrapper {
              width: 100% !important;
            }
          `}</style>

          <div className="d-flex flex-column" style={{ gap: '24px' }}>
            {/* 담당자 */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <i className="fas fa-user-tie text-primary fs-5 me-2"></i>
                  <label className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>담당자 <span className="text-danger">*</span></label>
                </div>
                <div className="position-relative" ref={popoverRef}>
                  {isEditing && (
                    <button className="btn btn-sm btn-light text-primary fw-bold fs-8 px-3 py-1 shadow-sm" onClick={() => {
                      if (showMemberPopover) {
                        setShowMemberPopover(false);
                      } else {
                        setTempMembers([...localMembers]);
                        setShowMemberPopover(true);
                        setTimeout(() => {
                          if (popoverRef.current) {
                            const rect = popoverRef.current.getBoundingClientRect();
                            if (window.innerHeight - rect.bottom < 330) {
                              setPopoverPosition('top');
                            } else {
                              setPopoverPosition('bottom');
                            }
                          }
                        }, 0);
                      }
                    }}>
                      <i className="fas fa-plus me-1"></i>추가
                    </button>
                  )}
                  {showMemberPopover && (
                    <div className="position-absolute shadow-lg bg-white rounded-3 p-0" style={{
                      [popoverPosition === 'top' ? 'bottom' : 'top']: '100%',
                      right: 0,
                      marginTop: popoverPosition === 'bottom' ? '8px' : 0,
                      marginBottom: popoverPosition === 'top' ? '8px' : 0,
                      width: '280px',
                      zIndex: 1050,
                      border: '1px solid #c2d7fa',
                      overflow: 'hidden'
                    }}>
                      <div className="p-2 border-bottom" style={{ backgroundColor: '#e8f0fe' }}>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                          <input type="text" className={`form-control border-start-0 ps-0 shadow-none ${memberSearchTerm ? 'border-end-0' : ''}`} placeholder="담당자를 입력하세요" value={memberSearchTerm} onChange={(e) => setMemberSearchTerm(e.target.value)} />
                          {memberSearchTerm && (
                            <span className="input-group-text bg-white border-start-0 cursor-pointer" onClick={() => setMemberSearchTerm('')} title="검색 초기화">
                              <i className="fas fa-times-circle text-muted" style={{ opacity: 0.6 }}></i>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {filteredProjectUsers.length > 0 ? [...filteredProjectUsers].sort((a, b) => a.name.localeCompare(b.name, 'ko')).map(u => {
                          const uIdStr = String(u.userid || u.id);
                          const isSelected = tempMembers.some(m => String(m.user_id) === uIdStr);
                          return (
                            <div key={uIdStr} className={`d-flex align-items-center px-2 py-1 mb-0 rounded cursor-pointer ${isSelected ? '' : 'hover-bg-light'}`} style={{ backgroundColor: isSelected ? '#e8f0fe' : 'transparent', border: isSelected ? '1px solid #d2e3fc' : '1px solid transparent' }} onClick={() => toggleTempMemberSelection(u)}>
                              <div className="flex-shrink-0 me-2">
                                {isSelected ? (
                                  <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '24px', height: '24px' }}>
                                    <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                                  </div>
                                ) : (
                                  <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm" style={{ width: '24px', height: '24px', backgroundColor: getRandomColor(u.name), fontSize: '11px' }}>
                                    {u.name?.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <div className="flex-grow-1 min-w-0">
                                <div className={`fw-bold fs-8 text-truncate ${isSelected ? 'text-primary' : 'text-dark'}`} title={`${u.name} ${u.org?.position || ''}${u.org?.dept_name ? `[${u.org.dept_name}]` : ''}`}>
                                  {u.name} {u.org?.position || ''}{u.org?.dept_name ? `[${u.org.dept_name}]` : ''}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="flex-shrink-0 ms-2">
                                  <button className="btn btn-sm btn-link text-danger p-0 d-flex align-items-center justify-content-center" title="선택 해제" onClick={(e) => { e.stopPropagation(); toggleTempMemberSelection(u); }}>
                                    <i className="fas fa-times fs-8"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        }) : (
                          <div className="text-center text-muted p-3 fs-8">지정 가능한 멤버가 없습니다.</div>
                        )}
                      </div>
                      <div className="p-2 border-top d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f0fe' }}>
                        <span className="fs-8 text-muted fw-bold">선택된 수: <strong className="text-primary ms-1">{tempMembers.length}</strong></span>
                        <button className="btn btn-sm btn-primary py-1 px-3 fs-8 fw-bold" onClick={() => { setLocalMembers(tempMembers); setShowMemberPopover(false); }}>선택 완료</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="member-scroll-box" style={isEditing ? { backgroundColor: '#fff9e6' } : undefined}>
                {localMembers.length > 0 ? (
                  localMembers.map(m => (
                    <div key={m.user_id} className="member-badge">
                      <div className="member-badge-info" title={`${m.user?.name} / ${m.user?.org?.position || m.user?.position || ''} ${m.user?.org?.dept_name ? '(' + m.user.org.dept_name + ')' : ''}`}>
                        <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                        <span className="text-truncate">{m.user?.name} / {m.user?.org?.position || m.user?.position || ''} {m.user?.org?.dept_name ? `(${m.user.org.dept_name})` : ''}</span>
                      </div>
                      {isEditing && (
                        <button className="btn btn-link text-muted p-0 ms-2 flex-shrink-0" onClick={() => handleRemoveMember(m.user_id, 'members')}><i className="fas fa-times fs-8"></i></button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="w-100 d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: '60px' }}>
                    <i className="fas fa-user-slash mb-2 fs-4 opacity-50"></i>
                    <span className="fs-7 fw-medium">지정된 담당자가 없습니다.</span>
                  </div>
                )}
              </div>
            </div>

            {/* 참조자 */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <i className="fas fa-share-square text-info fs-5 me-2"></i>
                  <label className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>참조자</label>
                </div>
                {isEditing && (
                  <button className="btn btn-sm btn-light text-primary fw-bold fs-8 px-3 py-1 shadow-sm" onClick={() => { setOrgTarget('observers'); setShowOrgModal(true); }}>
                    <i className="fas fa-plus me-1"></i>추가
                  </button>
                )}
              </div>
              <div className="member-scroll-box">
                {localObservers.length > 0 ? (
                  localObservers.map(m => (
                    <div key={m.user_id} className="member-badge">
                      <div className="member-badge-info" title={`${m.user?.name} / ${m.user?.org?.position || m.user?.position || ''} ${m.user?.org?.dept_name ? '(' + m.user.org.dept_name + ')' : ''}`}>
                        <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                        <span className="text-truncate">{m.user?.name} / {m.user?.org?.position || m.user?.position || ''} {m.user?.org?.dept_name ? `(${m.user.org.dept_name})` : ''}</span>
                      </div>
                      {isEditing && (
                        <button className="btn btn-link text-muted p-0 ms-2 flex-shrink-0" onClick={() => handleRemoveMember(m.user_id, 'observers')}><i className="fas fa-times fs-8"></i></button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="w-100 d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: '60px' }}>
                    <i className="fas fa-user-slash mb-2 fs-4 opacity-50"></i>
                    <span className="fs-7 fw-medium">지정된 참조자가 없습니다.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 조직도 모달 */}
        {showOrgModal && (
          <OrgSelect
            onConfirm={handleConfirmOrgSelect}
            onCancel={() => setShowOrgModal(false)}
            selectionMode="multiple"
            targetType="user"
            initialSelected={(orgTarget === 'members' ? localMembers : localObservers).map(m => ({
              target_type: 'user',
              target_id: String(m.user_id),
              target_name: m.user?.name,
              position: m.user?.position,
              dept_name: m.user?.org?.dept_name
            }))}
          />
        )}
      </div>

      {/* 하단 버튼 영역 */}
      <div className="px-3 py-2 bg-light d-flex justify-content-end gap-2 mt-auto" style={{ borderTop: '1px solid #adb5bd' }}>
        {isEditing && (
          <button className="btn btn-dark fw-bold px-3 shadow-sm me-auto" onClick={() => {
            // 취소 시 상태 초기화
            setFormData({
              title: task.title || '',
              description: task.description || '',
              startDate: task.start_date ? new Date(task.start_date) : null,
              endDate: task.end_date ? new Date(task.end_date) : null,
              status: task.status || 'pending',
              sortOrder: task.sort_order || 10,
            });
            setIsEditing(false);
          }}>뒤로가기</button>
        )}
        {hasEditPermission && !isEditing && (
          <button className="btn btn-outline-danger fw-bold px-3 shadow-sm me-auto" onClick={onDeleteTask}>
            <i className="fas fa-trash-alt me-1"></i>삭제
          </button>
        )}
        <button className="btn btn-secondary fw-bold px-3 shadow-sm" onClick={onClose}>닫기</button>
        {hasEditPermission && !isEditing && (
          <button className="btn btn-primary fw-bold px-3 shadow-sm" onClick={() => setIsEditing(true)}>
            <i className="fas fa-edit me-1"></i>수정하기
          </button>
        )}
        {isEditing && (
          <button className="btn btn-primary fw-bold px-3 shadow-sm" onClick={async () => {
            let finalStatus = formData.status;
            let finalProgress = parseInt(progressRate, 10);
            if (finalStatus === 'completed') {
              finalProgress = 100;
            } else if (finalProgress === 100) {
              finalStatus = 'completed';
            }
            const fullFormData = {
              ...formData,
              status: finalStatus,
              watchers: [
                ...localMembers.map(m => ({ ...m, is_manager: true })),
                ...localObservers.map(m => ({ ...m, is_manager: false }))
              ]
            };
            const success = await handleSaveProgress(fullFormData, finalProgress);
            if (success) setIsEditing(false);
          }}>
            <i className="fas fa-check me-1"></i>저장
          </button>
        )}
      </div>
    </div>
  );
}

export default SettingsTab;
